/**
 * Filters and sorting (snippets/facets.liquid): every change applies at once.
 *
 * The form's state becomes the URL, the section is fetched through the
 * Section Rendering API (?section_id=…) and two regions are swapped: the
 * filter bar with its chips ([data-facets]) and the results
 * ([data-facets-results]). The open panel stays open and focus returns to
 * the control that was used. Back and forward work (pushState / popstate).
 * Without JavaScript the form submits normally.
 *
 * After a swap it fires "bunz:content-updated": fade-up.js, shop-qol.js and
 * media-shimmer.js pick up the new cards. Idempotent; never logs.
 */
(() => {
  if (window.__bunzFacets) return;
  window.__bunzFacets = true;

  let controller = null;

  const formUrl = (form) => {
    const params = new URLSearchParams();
    for (const [key, value] of new FormData(form)) {
      if (String(value).trim() !== '') params.append(key, value);
    }
    const query = params.toString();
    return form.action.split('?')[0] + (query ? `?${query}` : '');
  };

  const swap = (doc, selector) => {
    const now = document.querySelector(selector);
    const next = doc.querySelector(selector);
    if (now && next) now.replaceWith(next);
  };

  async function render(url, push = true) {
    const facets = document.querySelector('[data-facets]');
    if (!facets) {
      window.location.href = url;
      return;
    }
    const target = new URL(url, window.location.href);
    target.searchParams.set('section_id', facets.dataset.sectionId);

    const open = [...document.querySelectorAll('[data-facet][open]')].map((d) => d.dataset.facet);
    const focusId = document.activeElement ? document.activeElement.id : '';
    const results = document.querySelector('[data-facets-results]');
    if (results) results.setAttribute('aria-busy', 'true');

    if (controller) controller.abort();
    controller = new AbortController();
    try {
      const response = await fetch(target, { signal: controller.signal });
      if (!response.ok) throw new Error(String(response.status));
      const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
      swap(doc, '[data-facets]');
      swap(doc, '[data-facets-results]');
    } catch (error) {
      if (error.name === 'AbortError') return;
      window.location.href = url; // the plain page load still works
      return;
    }

    document.querySelectorAll('[data-facet]').forEach((details) => {
      if (open.includes(details.dataset.facet)) details.open = true;
    });
    if (focusId) {
      const control = document.getElementById(focusId);
      if (control) control.focus({ preventScroll: true });
    }
    if (push) window.history.pushState({ facets: true }, '', url);
    document.dispatchEvent(new CustomEvent('bunz:content-updated'));
  }

  document.addEventListener('change', (event) => {
    const form = event.target.closest('[data-facets-form]');
    if (form) render(formUrl(form));
  });

  document.addEventListener('submit', (event) => {
    const form = event.target.closest('[data-facets-form]');
    if (!form) return;
    event.preventDefault();
    render(formUrl(form));
  });

  document.addEventListener('click', (event) => {
    const link = event.target.closest('a[data-facets-link]');
    if (link) {
      event.preventDefault();
      render(link.href);
      return;
    }
    // one panel at a time; a click anywhere else closes it
    const inside = event.target.closest('[data-facet]');
    document.querySelectorAll('[data-facet][open]').forEach((details) => {
      if (details !== inside) details.open = false;
    });
  });

  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape') return;
    const open = document.querySelector('[data-facet][open]');
    if (!open) return;
    open.open = false;
    const summary = open.querySelector('summary');
    if (summary) summary.focus();
  });

  window.addEventListener('popstate', () => {
    if (document.querySelector('[data-facets]')) render(window.location.href, false);
  });
})();
